/**
 * Material Warnings Component
 *
 * Displays prohibited materials, pricing warnings, and required acknowledgments
 * in the order builder workflow. Provides clear visual indicators and accessible
 * interactions for important order information.
 */

import { useState } from 'react';
import { Ban, AlertTriangle, ChevronDown } from 'lucide-react';
import type { PricingWarning } from '../../types/pricing-estimation';

/**
 * Props for MaterialWarnings component
 */
export interface MaterialWarningsProps {
  /** List of prohibited materials for the selected service */
  prohibitedMaterials?: string[];
  /** Pricing warnings from estimation response */
  warnings?: PricingWarning[];
  /** Required acknowledgments user must accept */
  requiredAcknowledgments?: string[];
  /** Currently acknowledged items */
  acknowledgedItems?: string[];
  /** Callback when acknowledgment is toggled */
  onAcknowledgmentToggle?: (acknowledgment: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * MaterialWarnings Component
 *
 * Renders important information about materials and order warnings:
 * - Collapsible prohibited materials section (red)
 * - Pricing warnings with severity-based styling
 * - Required acknowledgments with checkboxes
 *
 * Only renders if there is content to display.
 */
export function MaterialWarnings({
  prohibitedMaterials = [],
  warnings = [],
  requiredAcknowledgments = [],
  acknowledgedItems = [],
  onAcknowledgmentToggle,
  className = '',
}: MaterialWarningsProps) {
  const [isProhibitedOpen, setIsProhibitedOpen] = useState(false);

  // Don't render if no content to display
  const hasContent =
    prohibitedMaterials.length > 0 ||
    warnings.length > 0 ||
    requiredAcknowledgments.length > 0;

  if (!hasContent) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <h3 className="text-lg font-medium text-gray-900">Important Information</h3>

      {/* Prohibited Materials (collapsible) */}
      {prohibitedMaterials.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50/50 p-4">
          <button
            onClick={() => setIsProhibitedOpen(!isProhibitedOpen)}
            className="flex w-full items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded"
            aria-expanded={isProhibitedOpen}
            aria-controls="prohibited-materials-list"
            type="button"
          >
            <div className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-600" aria-hidden="true" />
              <span className="font-medium text-red-900">
                Prohibited Materials ({prohibitedMaterials.length})
              </span>
            </div>
            <ChevronDown
              className={`h-4 w-4 text-red-600 transition-transform duration-200 ${
                isProhibitedOpen ? 'rotate-180' : ''
              }`}
              aria-hidden="true"
            />
          </button>

          {isProhibitedOpen && (
            <ul
              id="prohibited-materials-list"
              className="mt-3 space-y-1 text-sm text-red-800"
              role="list"
            >
              {prohibitedMaterials.map((material, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-red-600" aria-hidden="true">
                    •
                  </span>
                  <span>{material}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Pricing Warnings */}
      {warnings.map((warning, idx) => {
        const isError = warning.type === 'CONTAMINATION' || warning.severity === 'ERROR';
        const isWarning = warning.severity === 'WARNING';

        const borderColor = isError
          ? 'border-red-200'
          : isWarning
          ? 'border-yellow-200'
          : 'border-blue-200';

        const bgColor = isError
          ? 'bg-red-50/50'
          : isWarning
          ? 'bg-yellow-50/50'
          : 'bg-blue-50/50';

        const iconColor = isError
          ? 'text-red-600'
          : isWarning
          ? 'text-yellow-600'
          : 'text-blue-600';

        const textColor = isError
          ? 'text-red-900'
          : isWarning
          ? 'text-yellow-900'
          : 'text-blue-900';

        return (
          <div
            key={idx}
            className={`rounded-lg border p-4 ${borderColor} ${bgColor}`}
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle
                className={`h-5 w-5 mt-0.5 flex-shrink-0 ${iconColor}`}
                aria-hidden="true"
              />
              <div className="flex-1">
                <p className={`font-medium ${textColor}`}>{warning.message}</p>
                {warning.estimatedCostImpact !== undefined &&
                  warning.estimatedCostImpact > 0 && (
                    <p className="text-sm mt-1 text-gray-600">
                      Estimated cost impact: ${(warning.estimatedCostImpact / 100).toFixed(2)}
                    </p>
                  )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Required Acknowledgments */}
      {requiredAcknowledgments.length > 0 && (
        <div className="space-y-2" role="group" aria-labelledby="acknowledgments-heading">
          <h4 id="acknowledgments-heading" className="sr-only">
            Required Acknowledgments
          </h4>
          {requiredAcknowledgments.map((ack, idx) => {
            const isChecked = acknowledgedItems?.includes(ack) || false;
            const checkboxId = `ack-${idx}`;

            return (
              <label
                key={idx}
                htmlFor={checkboxId}
                className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2"
              >
                <input
                  id={checkboxId}
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onAcknowledgmentToggle?.(ack)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  aria-required="true"
                />
                <span className="text-sm text-gray-700">{ack}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
